import React, { useState,useEffect } from 'react'
import Creatable from 'react-select/creatable'
import { DropdownIndicator, Placeholder } from './styles/select_styles'
import type { OptionType } from '../../../common/types/utilities'
import { ActionMeta, GroupTypeBase, StylesConfig } from 'react-select'
import styled from 'styled-components'
import { getCurrentValue } from './helper'

const CLASS_PREFIX = `ListAsync--styledMulitSelectCreatable`

interface Props<T> {
    value: OptionType | OptionType[]
    onChange: (option: OptionType | OptionType[], actionMeta: ActionMeta<any>) => void
    placeholder?: string
    getApi: () => Promise<any>
    callback: (json: T) => OptionType[]
    isClearable?: boolean
    disabled?: boolean
    isMulti?: boolean
    editMode: boolean
    useLabels?: boolean
    styles?: StylesConfig<OptionType, boolean, GroupTypeBase<OptionType>>
    variant?: '' | 'secondary',
    selectValueById?:boolean
}

export default function ListAsync<T = any>({
    callback,
    getApi,
    onChange,
    placeholder,
    value,
    disabled = false,
    isMulti = false,
    editMode,
    isClearable,
    useLabels = true,
    styles,
    variant = '',
    selectValueById = false
}: Props<T>): JSX.Element {
    const [options, setOptions] = useState<OptionType[] | null>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [currentValue, setValue] = useState<OptionType[] | OptionType >(value)    

    const updateOptions = (): void => {
        if(loading) return
        setLoading(true)
        getApi()
            .then(response => callback(response))
            .then(newOptions =>
                setOptions(
                    newOptions.reduce((result, current) => {
                        result.push({
                            value: current.value,
                            label: useLabels ? current.label : current.value.toString()
                        })

                        return result
                    }, [] as OptionType[])
                )
            )
            .catch(error => {
                console.error(error)
                setOptions([])
            })
            .finally(() => setLoading(false))
    }
    
    useEffect(() => {   
        setValue(() => getCurrentValue(value,options,isMulti,selectValueById))
    },[options,value])    

    useEffect(() => {              
         updateOptions()
    },[])    
    
    switch (variant) {
        case 'secondary':
            return (
                <StyledCreatableSecondary
                    classNamePrefix={CLASS_PREFIX}
                    isClearable={isClearable}
                    isLoading={loading}
                    onMenuOpen={() => updateOptions()}                    
                    isDisabled={disabled}
                    value={currentValue}
                    placeholder={placeholder}
                    onChange={onChange}
                    isMulti={isMulti}
                    closeMenuOnSelect={!isMulti}
                    maxMenuHeight={150}
                    styles={styles}
                    components={{ Placeholder, DropdownIndicator }}
                    options={options}
                />
            )
        case '':
        default:
            return (
                <StyledCreatable
                    classNamePrefix={CLASS_PREFIX}
                    isClearable={isClearable}
                    isLoading={loading}
                    onMenuOpen={() => updateOptions()}
                    isDisabled={disabled}
                    value={currentValue}
                    placeholder={placeholder}
                    onChange={onChange}
                    isMulti={isMulti}
                    closeMenuOnSelect={!isMulti}
                    maxMenuHeight={150}
                    styles={styles}
                    components={!editMode ? { Placeholder } : { Placeholder, DropdownIndicator }}
                    options={options}
                />
            )
    }
}

const StyledCreatable = styled(Creatable)`
    border: 1px solid transparent;
    border-left: 1px solid ${({ theme }) => theme['gray-500']};
    border-bottom: 1px solid ${({ theme }) => theme['gray-500']};
    &:focus-within {
        border-width: 2px;
        border-color: ${({ theme }) => theme['blue-500']};
    }
    .${CLASS_PREFIX}__option {
        color: ${props => props.theme['blue-500']};
        &--is-selected {
            color: ${props => props.theme['gray-0']};
        }
    }
    .${CLASS_PREFIX}__control {
        border: none;
        background: ${({ theme }) => theme['gray-0']};
        height: 50px;
        max-height: 50px;
        overflow: auto;
        border-radius: 0px;

        &::-webkit-scrollbar {
            width: 14px;
        }

        &::-webkit-scrollbar-thumb {
            background: ${({ theme }) => theme['gray-600']};
            border-radius: 25px;
            border: 4px solid transparent;
            background-clip: padding-box;
        }
    }
    .${CLASS_PREFIX}__indicator-separator {
        display: none;
    }
    .${CLASS_PREFIX}__indicators {
        display: flex;
    }
`

const StyledCreatableSecondary = styled(Creatable)`
    background: transparent;

    .${CLASS_PREFIX}__option {
        color: ${props => props.theme['blue-500']};
        &--is-selected {
            color: ${props => props.theme['gray-0']};
        }
    }
    .${CLASS_PREFIX}__control {
        height: 35px;
        min-height: 35px;
        background: transparent;
        &,
        &--is-focused {
            border: none;
            box-shadow: none;
        }
    }
    .${CLASS_PREFIX}__indicator-separator {
        display: none;
    }
    .${CLASS_PREFIX}__indicators {
        display: flex;
    }
`
